import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Upload, Trash2, Edit2, CheckCircle, XCircle } from "lucide-react";
import {
  uploadArquivo,
  ArquivoData,
} from "@/integrations/supabase/arquivos";
import { toast } from "sonner";

interface ArquivosSectionLocalProps {
  arquivos: ArquivoData[];
  onArquivosUpdated: (arquivos: ArquivoData[]) => void;
  isExpanded?: boolean;
}

export default function ArquivosSectionLocal({
  arquivos,
  onArquivosUpdated,
  isExpanded = false,
}: ArquivosSectionLocalProps) {
  const [isExpanding, setIsExpanding] = useState(isExpanded);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [posicao, setPosicao] = useState("");
  const [descricao, setDescricao] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editPosicao, setEditPosicao] = useState("");
  const [editDescricao, setEditDescricao] = useState("");

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !posicao.trim()) {
      toast.error("Selecione um arquivo e informe a posição");
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    try {
      const uploadResult = await uploadArquivo(selectedFile, posicao, descricao);

      if (!uploadResult.ok) {
        toast.error(uploadResult.error || "Erro no upload");
        setIsUploading(false);
        return;
      }

      const novoArquivo = uploadResult.data!;

      // Simular progress para UI feedback
      for (let i = 0; i < 100; i++) {
        await new Promise((resolve) => setTimeout(resolve, 10));
        setUploadProgress(i + 1);
      }

      // Atualizar lista local (sem tentar salvar no banco)
      const arquivosAtualizados = [...arquivos, novoArquivo];
      onArquivosUpdated(arquivosAtualizados);

      toast.success("Arquivo pronto para ser salvo!");
      setSelectedFile(null);
      setPosicao("");
      setDescricao("");
      setUploadProgress(0);
    } catch (error: any) {
      toast.error(error?.message || "Erro ao fazer upload");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = (arquivoId: string) => {
    if (!confirm("Tem certeza que deseja remover este arquivo?")) return;

    const arquivosAtualizados = arquivos.filter((a) => a.id !== arquivoId);
    onArquivosUpdated(arquivosAtualizados);
    toast.success("Arquivo removido!");
  };

  const handleSaveEdit = (arquivoId: string) => {
    const arquivosAtualizados = arquivos.map((a) =>
      a.id === arquivoId
        ? { ...a, posicao: editPosicao, descricao: editDescricao }
        : a
    );
    onArquivosUpdated(arquivosAtualizados);
    toast.success("Arquivo atualizado!");
    setEditingId(null);
  };

  const startEdit = (arquivo: ArquivoData) => {
    setEditingId(arquivo.id);
    setEditPosicao(arquivo.posicao);
    setEditDescricao(arquivo.descricao || "");
  };

  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">Arquivo / Arte</CardTitle>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanding(!isExpanding)}
          >
            {isExpanding ? "▼" : "▶"}
          </Button>
        </div>
      </CardHeader>

      {isExpanding && (
        <CardContent className="space-y-4">
          {/* Upload Form */}
          <div className="space-y-3 border rounded-lg p-4 bg-muted/50">
            <div>
              <Label htmlFor="arquivo">Selecionar arquivo</Label>
              <input
                id="arquivo"
                type="file"
                onChange={handleFileChange}
                disabled={isUploading}
                accept="image/*,.pdf"
                className="hidden"
              />
              <label
                htmlFor="arquivo"
                className={`block border-2 border-dashed rounded-lg p-4 text-center transition-colors cursor-pointer ${
                  isUploading
                    ? "border-blue-400 bg-blue-50"
                    : selectedFile
                    ? "border-green-400 bg-green-50"
                    : "border-input hover:border-secondary"
                }`}
              >
                {isUploading ? (
                  <div className="space-y-2">
                    <div className="w-6 h-6 mx-auto border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                    <p className="text-sm text-blue-600 font-medium">
                      Enviando {Math.round(uploadProgress)}%
                    </p>
                  </div>
                ) : selectedFile ? (
                  <div className="space-y-1">
                    <CheckCircle className="w-6 h-6 mx-auto text-green-600" />
                    <p className="text-sm text-green-600 font-medium">
                      {selectedFile.name}
                    </p>
                  </div>
                ) : (
                  <div>
                    <Upload className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      Clique ou arraste o arquivo
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      PNG, JPG, PDF até 10MB
                    </p>
                  </div>
                )}
              </label>
            </div>

            <div>
              <Label htmlFor="posicao">Posição / Localização *</Label>
              <Input
                id="posicao"
                placeholder="ex: Frente, Verso, Logo, Etiqueta..."
                value={posicao}
                onChange={(e) => setPosicao(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <div>
              <Label htmlFor="descricao">Descrição (opcional)</Label>
              <Input
                id="descricao"
                placeholder="ex: Logo do cliente, arte da tampa..."
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                disabled={isUploading}
              />
            </div>

            <Button
              onClick={handleUpload}
              disabled={!selectedFile || !posicao.trim() || isUploading}
              className="w-full bg-blue-600 hover:bg-blue-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              Enviar arquivo
            </Button>
          </div>

          {/* Lista de Arquivos */}
          {arquivos.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm font-medium text-muted-foreground">
                Arquivos ({arquivos.length})
              </p>
              <div className="space-y-2">
                {arquivos.map((arquivo) => (
                  <div
                    key={arquivo.id}
                    className="border rounded-lg p-3 space-y-2 bg-background"
                  >
                    {editingId === arquivo.id ? (
                      <div className="space-y-2">
                        <Input
                          placeholder="Posição"
                          value={editPosicao}
                          onChange={(e) => setEditPosicao(e.target.value)}
                        />
                        <Input
                          placeholder="Descrição"
                          value={editDescricao}
                          onChange={(e) => setEditDescricao(e.target.value)}
                        />
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => handleSaveEdit(arquivo.id)}
                            className="flex-1 bg-green-600 hover:bg-green-700"
                          >
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Salvar
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setEditingId(null)}
                            className="flex-1"
                          >
                            Cancelar
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm truncate">
                              {arquivo.nome}
                            </p>
                            <p className="text-xs text-blue-600 font-semibold mt-1">
                              {arquivo.posicao}
                            </p>
                            {arquivo.descricao && (
                              <p className="text-xs text-muted-foreground mt-1">
                                {arquivo.descricao}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-1">
                              {arquivo.tamanho_kb}KB
                            </p>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEdit(arquivo)}
                            >
                              <Edit2 className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(arquivo.id)}
                            >
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </div>
                        <a
                          href={arquivo.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-xs text-blue-600 hover:underline block truncate"
                        >
                          Ver arquivo
                        </a>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
